import { customEvents } from '../../config/custom-events';
import switchLinksTabIndex from '../../utils/switchLinksTabIndex';
import { config } from './config';
import { breakpoints } from '../../config/breakpoints';
import { showOverlay, hideOverlay } from '../../utils/pageOverlay';
import keyboardFocusCallback from '../../utils/keyboardFocusCallback';
import fadeNonActiveLinks from '../../utils/fadeNonActiveLinks';

export default function secondary() {
  /**
   * Secondary navigation wrapper class
   * @type {string}
   */
  const navigationSelector = '.js-secondary-navigation';

  /**
   * Header secondary navigation link selector (to assign fade class)
   * @type {string}
   */
  const headerSecondaryNavLinkSelector = '.js-header-secondary-navigation-link';

  /**
   * Header secondary navigation link fade class
   * @type {string}
   */
  const headerSecondaryNavLinkFadeClass = 'navigation__link--state-faded';

  /**
   * Secondary navigation toggles selector (keyboard navigation)
   * @type {string}
   */
  const keyboardToggleSelector = '.js-header-tab-navigation-toggle';

  /**
   * Destinations link selector
   * @type {string}
   */
  const linkSelector = '.js-secondary-inner-nav-link';

  /**
   * Selector for links inside image grid layout (with image)
   * @type {string}
   */
  const imageGridLinkSelector = '.js-navigation-image-grid-link';

  /**
   * Opacity effect for image grid links
   */
  fadeNonActiveLinks(imageGridLinkSelector, 'navigation__link--state-faded');

  /**
   * Events handlers
   */
  document.addEventListener(customEvents.navigation.secondary.open, (event) => {
    const { toggle, isKeyboardNavigation } = event.detail;
    const navigation = toggle.querySelector(navigationSelector);
    const toggleLink = toggle.querySelector(headerSecondaryNavLinkSelector);
    const primaryHeaderLevelNav = document.querySelector(config.headerPrimaryLevelSelector);

    if (primaryHeaderLevelNav) {
      primaryHeaderLevelNav.classList.add(config.headerLevelFadeClass);
    }

    // close main navigation
    document.dispatchEvent(new Event(customEvents.navigation.main.close));
    toggle.setAttribute('aria-expanded', 'true');
    navigation.classList.add(config.navigationOpenModifierClass);
    switchLinksTabIndex(navigation, '0', 100);
    if (isKeyboardNavigation) {
      toggle.querySelector(keyboardToggleSelector).setAttribute('data-active', 'true');
    }
    showOverlay();

    if (window.screen.width > breakpoints.md) {
      import('../../utils/changeImageOnLinkFocus').then((module) => {
        module.default(
          linkSelector,
          navigation.querySelector(config.imageHoverPlaceholderSelector),
          config.hoverCaptionPlaceholderSelector,
        );
      });

      document.querySelectorAll(headerSecondaryNavLinkSelector).forEach((link) => {
        if (!link.isEqualNode(toggleLink)) {
          link.classList.add(headerSecondaryNavLinkFadeClass);
        }
      });
    }
    document.querySelector(config.body).classList.add(config.bodyLockScrollClass);
  });

  document.addEventListener(customEvents.navigation.secondary.close, (event) => {
    const { toggle, isKeyboardNavigation } = event.detail;
    const navigation = toggle.querySelector(navigationSelector);
    const image = navigation.querySelector(config.imageHoverPlaceholderSelector);
    const caption = navigation.querySelector(config.hoverCaptionPlaceholderSelector);
    const primaryHeaderLevelNav = document.querySelector(config.headerPrimaryLevelSelector);

    if (image) {
      image.style.backgroundImage = '';
    }

    if (caption) {
      caption.innerHTML = '';
    }

    if (primaryHeaderLevelNav) {
      primaryHeaderLevelNav.classList.remove(config.headerLevelFadeClass);
    }

    toggle.setAttribute('aria-expanded', 'false');
    navigation.classList.remove(config.navigationOpenModifierClass);
    switchLinksTabIndex(navigation, '-1', 100);

    if (isKeyboardNavigation) {
      toggle.querySelector(keyboardToggleSelector).setAttribute('data-active', 'false');
    }

    hideOverlay();

    document.querySelectorAll(headerSecondaryNavLinkSelector).forEach((link) => {
      link.classList.remove(headerSecondaryNavLinkFadeClass);
    });
    document.querySelector(config.body).classList.remove(config.bodyLockScrollClass);
  });

  document.addEventListener('DOMContentLoaded', () => {
    const navigations = document.querySelectorAll(navigationSelector);

    navigations.forEach((navigation) => {
      const closeMenuKeyboardFocusCallback = () => {
        // eslint-disable-next-line max-len
        const keyboardNavigationToggle = navigation.parentNode.querySelector(keyboardToggleSelector);

        setTimeout(() => {
          keyboardNavigationToggle.focus();
        }, 1);
      };

      const items = navigation.querySelectorAll(linkSelector);
      const isAnyItemHasImage = Array.from(items).some((item) => item.getAttribute('data-image-url') !== '');

      // adds height to the image holder
      if (isAnyItemHasImage) {
        navigation.querySelector(config.imageHoverPlaceholderSelector).classList.add('navigation__hover-image-placeholder--state-visible');
      }

      keyboardFocusCallback(
        navigation,
        closeMenuKeyboardFocusCallback,
        closeMenuKeyboardFocusCallback,
      );
    });
  });
}
